import { mapService } from "@/services/map/mapService";
import TileWMS from "ol/source/TileWMS.js";
import {
    Tile as TileLayer,
    Vector as VectorLayer,
} from "ol/layer.js";
import { ImageArcGISRest, TileArcGISRest } from "ol/source.js";
import ImageLayer from 'ol/layer/Image.js';
import VectorSource from "ol/source/Vector";
import EsriJSON from "ol/format/EsriJSON";
import ImageWMS from 'ol/source/ImageWMS.js';
import { bbox as bboxStrategy } from "ol/loadingstrategy";
import KML from "ol/format/KML.js";
import { unzipSync } from "fflate";
import JSZip from "jszip";
import * as shapefile from "shapefile";
import GeoJSON from "ol/format/GeoJSON.js";
import { UrlHandler } from "@/services/utils/UrlHandler.js"


let kmzZipFiles = null;

function extractKmlFromKmz(arrayBuffer) {
    kmzZipFiles = unzipSync(new Uint8Array(arrayBuffer));

    const kmlName = Object.keys(kmzZipFiles).find(f =>
        f.toLowerCase().endsWith(".kml")
    );

    if (!kmlName) return null;

    return new TextDecoder().decode(kmzZipFiles[kmlName]);
}

function resolveKmzImage(href) {
    const imgName = href.split("/").pop();
    if (!kmzZipFiles || !kmzZipFiles[imgName]) return href;

    return URL.createObjectURL(new Blob([kmzZipFiles[imgName]]));
}

class KMZFormat extends KML {
    constructor(options = {}) {
        options.iconUrlFunction = resolveKmzImage;
        super(options);
    }

    getType() {
        return "arraybuffer";
    }

    readFeatures(buffer, options) {
        const kmlText = extractKmlFromKmz(buffer);
        return super.readFeatures(kmlText, options);
    }
}



export const LayerService = {
    isValidExtent(extent) {
        return (
            Array.isArray(extent) &&
            extent.length === 4 &&
            extent.every((c) => typeof c === "number" && !isNaN(c)) &&
            extent[0] < extent[2] &&
            extent[1] < extent[3]
        );
    },
    getLayerProvider(layer) {
        return layer.provider.name || "";
    },
    getLayerUrl(layer) {
        return layer.provider.url || "";
    },
    getGeoserverDataStore(layer) {
        return layer.provider.geoserverdata.datastore || "";
    },
    getGeoserverWorkspace(layer) {
        return layer.provider.geoserverdata.workspace || "";
    },
    getLayerArcGISCode(layer) {
        return layer.codigo || "0";
    },
    getLayerOrden(layer) {
        return layer.orden || 0;
    },
    getArcgisExtent(layer) {
        const queryUrl = UrlHandler.getArcgisUrl(layer) + "/query?where=1=1&returnExtentOnly=true&f=json&outSR=3857"
        console.log("Query URL:", queryUrl)
        fetch(queryUrl)
            .then((response) => response.json())
            .then((data) => {
                if (data.extent) {
                    const extent = [data.extent.xmin, data.extent.ymin, data.extent.xmax, data.extent.ymax];
                    if (this.isValidExtent(extent)) {
                        mapService.zoomToExtent(extent);
                    }
                } else {
                    console.warn("Extent no encontrado en respuesta ArcGIS:", data);
                }
            })
            .catch((error) => {
                console.error("Error al obtener el extent del layer:", error);
                return null;
            });

    },
    getGeoserverExtent(layer) {
        const baseUrl = UrlHandler.getBaseUrl(layer) + "/wms";
        const layersParam =
            this.getGeoserverWorkspace(layer) +
            ":" +
            this.getGeoserverDataStore(layer);
        const queryParams = {
            service: "WFS",
            version: "2.0.0",
            request: "GetFeature",
            typeNames: layersParam,
            outputFormat: "application/json",
            count: 1,
            startIndex: 0,
            srsName: "EPSG:3857",
        }
        const queryUrl = baseUrl + "?" + Object.entries(queryParams)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join("&");
        console.log("Query URL:", queryUrl)
        fetch(queryUrl)
            .then((response) => response.json())
            .then((data) => {
                const extent = data.bbox
                if (this.isValidExtent(extent)) {
                    mapService.zoomToExtent(extent)
                } else {
                    console.warn("Extent inválido recibido de GeoServer:", extent);
                }
            })
            .catch((error) => {
                console.error("Error al obtener el extent del layer:", error);
                return null;
            });
    },

    async getGeoServerLayerType(layer) {
        const params = {
            workspace: this.getGeoserverWorkspace(layer),
            datastore: this.getGeoserverDataStore(layer),
            url: this.getLayerUrl(layer)
        }
        const backendURL = process.env.VUE_APP_API_URL
        return fetch(backendURL + "api/v1/geoserver/layerType", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(params),
        })
            .then((response) => response.json())
            .then((data) => {
                console.log("Data:", data)
                return data.layerType
            })
            .catch((error) => {
                console.error("Error al obtener el tipo del layer:", error);
                return null;
            });
    },

    // Helper to build CQL filter from geometry
    buildCqlFilter(geometry) {
        if (!geometry || !geometry.coordinates) return "";
        try {
            const type = geometry.type;
            const coords = geometry.coordinates;
            // Assuming Polygon for now, need robust conversion for multipolygon, etc.
            if (type === "Polygon") {
                const coordStr = coords[0].map(c => c.join(" ")).join(",");
                return `INTERSECTS(the_geom, POLYGON((${coordStr})))`;
            }
        } catch (e) {
            console.error("Error building CQL filter:", e);
        }
        return "";
    },
    buildGeoserverVectorLayer(layer, filterGeometry = null) {
        const url = UrlHandler.getBaseUrl(layer) + "/wms";
        const layersParam =
            this.getGeoserverWorkspace(layer) +
            ":" +
            this.getGeoserverDataStore(layer);

        const params = {
            LAYERS: layersParam,
            TILED: true,
            VERSION: "1.1.1",
        };

        if (filterGeometry) {
            console.log("Building CQL filter with geometry:", filterGeometry);
            const cql = this.buildCqlFilter(filterGeometry);
            if (cql) {
                params.CQL_FILTER = cql;
                console.log("Applying CQL Filter:", cql);
            } else {
                console.warn("CQL Filter returned empty string.");
            }
        } else {
            console.log("No filterGeometry passed to buildGeoserverVectorLayer");
        }

        const wmsSource = new TileWMS({
            url,
            params: params,
            serverType: "geoserver",
            crossOrigin: "anonymous",
        });
        const layerToAdd = new TileLayer({
            source: wmsSource,
            opacity: 1,
            id: layer.id,
        })
        console.log("Legend URL:", wmsSource.getLegendUrl());
        layerToAdd.set('provider', 'geoserver');
        layerToAdd.set('layerType', 'vector');
        return layerToAdd
    },
    buildGeoserverRasterLayer(layer) {
        const baseURL = UrlHandler.getBaseUrl(layer)
        const workspace = this.getGeoserverWorkspace(layer)
        const datastore = this.getGeoserverDataStore(layer)
        const layersParam = `${workspace}:${datastore}`
        const url = `${baseURL}/wms/${workspace}`
        const layerToAdd = new ImageLayer({
            source: new ImageWMS({
                url: url,
                params: {
                    'LAYERS': layersParam,
                    'FORMAT': 'image/png',
                    'VERSION': '1.1.1'
                }
            }),
            opacity: 1,
            id: layer.id,
        });
        layerToAdd.set('provider', 'geoserver');
        layerToAdd.set('layerType', 'raster');
        return layerToAdd
    },



    buildGeoserverLayer(layer, layerType, filterGeometry = null) {
        console.log("Layer tipo:", layerType)
        if (layerType === "vector") {
            return this.buildGeoserverVectorLayer(layer, filterGeometry)
        }
        if (layerType === "raster") {
            return this.buildGeoserverRasterLayer(layer)
        }

        return null
    },
    async addLayerGeoserver(layer, filterGeometry = null, doZoom = true) {
        const ordenLayerInt = parseInt(this.getLayerOrden(layer));
        const layerType = await this.getGeoServerLayerType(layer)
        console.log("Layer tipo identificada:", layerType)
        if (layerType === null || layerType === undefined) {
            return
        }
        const layerToAdd = this.buildGeoserverLayer(layer, layerType, filterGeometry)
        console.log("Layer creada:", layerToAdd)
        if (layerType === "vector" && doZoom) {
            this.getGeoserverExtent(layer)
        }
        layerToAdd.setZIndex(ordenLayerInt);
        mapService.addLayerToMap(layerToAdd);
    },
    addLayerArcGISImageServer(layer) {
        const url = UrlHandler.getArcgisUrl(layer);
        const ordenLayerInt = parseInt(this.getLayerOrden(layer));
        const layerSource = new ImageArcGISRest({
            url: url,
        })
        const layerToAdd = new ImageLayer({
            source: layerSource,
            opacity: 1,
            id: layer.id,
        })
        layerToAdd.setZIndex(ordenLayerInt);
        mapService.addLayerToMap(layerToAdd);
    },
    addLayerArcGISMapServer(layer, filterGeometry = null, doZoom = true) {
        const url = UrlHandler.getBaseUrl(layer);
        const codigoLayer = this.getLayerArcGISCode(layer);
        const ordenLayerInt = parseInt(this.getLayerOrden(layer));

        const params = {
            LAYERS: "show:" + codigoLayer,
        };

        // Standard TileArcGISRest for all layers
        const layerSource = new TileArcGISRest({
            url: url,
            params: params,
        })
        const layerToAdd = new TileLayer({
            source: layerSource,
            opacity: 1,
            id: layer.id,
        })
        layerToAdd.set('provider', 'arcgis');
        layerToAdd.set('layerType', 'vector');
        layerToAdd.set('codigo', codigoLayer);
        if (doZoom) {
            this.getArcgisExtent(layer)
        }
        layerToAdd.setZIndex(ordenLayerInt);
        mapService.addLayerToMap(layerToAdd, filterGeometry); // Pass filterGeometry for client-side clipping
    },


    addLayerArcGISFeatureServer(layer, doZoom = true) {
        const ordenLayerInt = parseInt(this.getLayerOrden(layer));
        const fullUrl = UrlHandler.getArcgisUrl(layer);
        console.log("Full url:", fullUrl)
        const layerSource = new VectorSource({
            format: new EsriJSON(),
            url: function (extent, resolution, projection) {
                // ArcGIS Server only wants the numeric portion of the projection ID.
                const srid = projection
                    .getCode()
                    .split(/:(?=\d+$)/)
                    .pop();

                const url =
                    fullUrl +
                    '/query/?f=json&' +
                    'returnGeometry=true&spatialRel=esriSpatialRelIntersects&geometry=' +
                    encodeURIComponent(
                        '{"xmin":' +
                        extent[0] +
                        ',"ymin":' +
                        extent[1] +
                        ',"xmax":' +
                        extent[2] +
                        ',"ymax":' +
                        extent[3] +
                        ',"spatialReference":{"wkid":' +
                        srid +
                        '}}',
                    ) +
                    '&geometryType=esriGeometryEnvelope&inSR=' +
                    srid +
                    '&outFields=*' +
                    '&outSR=' +
                    srid;

                return url;
            },
            strategy: bboxStrategy,
        });

        const layerToAdd = new VectorLayer({
            source: layerSource,
            opacity: 1,
            id: layer.id,
        })
        if (doZoom) {
            this.getArcgisExtent(layer)
        }
        layerToAdd.setZIndex(ordenLayerInt);
        mapService.addLayerToMap(layerToAdd);
    },
    async checkUrlExists(layer) {
        const baseUrl = UrlHandler.getArcgisUrl(layer) + "?f=json"
        console.log("Full url:", baseUrl)
        const response = await fetch(baseUrl)
        const data = await response.json()
        return data.error === undefined
    },
    async addLayerArcgis(layer, filterGeometry = null, doZoom = true) {
        const baseUrl = UrlHandler.getBaseUrl(layer).toLowerCase();
        const exists = await this.checkUrlExists(layer)
        console.log("Existe:", exists)
        if (!exists) {
            console.log("Layer no existe")
            return
        }
        if (baseUrl.endsWith("/imageserver")) {
            this.addLayerArcGISImageServer(layer); // ImageServer usually raster, complex to filter by geometry on tile requets without Mosaic Rule
        }
        if (baseUrl.endsWith("/mapserver")) {
            this.addLayerArcGISMapServer(layer, filterGeometry, doZoom);
        }
        if (baseUrl.endsWith("/featureserver")) {
            this.addLayerArcGISFeatureServer(layer, doZoom); // Already uses bbox strategy, complex to inject custom geometry intersect on top of bbox cleanly without refactor
            // But let's check if we can add it to the custom loader function?
            // For now leaving FeatureServer as is or implementing if critical.
            // FeatureServer usually loads features by BBOX.
            // If we want RESTRICT to polygon, we can add geometry filter to the loader.
        }
    },
    addLayerToMap(layer, filterGeometry = null, doZoom = true) {
        const provider = this.getLayerProvider(layer);
        switch (provider) {
            case "geoserver":
                this.addLayerGeoserver(layer, filterGeometry, doZoom);
                break;
            case "arcgis":
                this.addLayerArcgis(layer, filterGeometry, doZoom);
                break;
            default:
                console.log("Provider no soportado:", provider);
        }
    },
    addLayerKml(file) {
        const id = crypto.randomUUID();
        console.log("Agregando capa KML desde archivo:", file.name);
        const vectorSource = new VectorSource({
            url: URL.createObjectURL(file),
            format: new KML(),
        });
        const vectorLayer = new VectorLayer({
            source: vectorSource,
            name: id,
        });
        vectorLayer.setZIndex(0);
        vectorSource.on('featuresloadend', function (event) {
            console.log("Características KML cargadas.");
            const extent = vectorSource.getExtent();
            mapService.zoomToExtent(extent);
        });
        mapService.addLayerToMap(vectorLayer);

        return vectorLayer;
    },

    async addLayerKmz(file) {
        console.log("Agregando capa KMZ:", file.name);

        const id = crypto.randomUUID();

        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const arrayBuffer = e.target.result;

                    // Crear formato KMZ (KML interno)
                    const kmzFormat = new KMZFormat();

                    // Extraer features del KMZ
                    const features = kmzFormat.readFeatures(arrayBuffer, {
                        featureProjection: "EPSG:3857",
                    });

                    if (!features || features.length === 0) {
                        console.warn("KMZ sin características o no válido");
                        resolve(null);
                        return;
                    }

                    // Crear la fuente y la capa
                    const vectorSource = new VectorSource({
                        features,
                    });

                    const vectorLayer = new VectorLayer({
                        source: vectorSource,
                        name: id,
                    });

                    // Agregar al mapa
                    mapService.get().addLayer(vectorLayer);

                    // Zoom al contenido
                    const extent = vectorSource.getExtent();
                    if (this.isValidExtent(extent)) {
                        mapService.zoomToExtent(extent);
                    }

                    console.log("KMZ cargado correctamente.");
                    resolve(vectorLayer);
                } catch (error) {
                    console.error("Error procesando KMZ:", error);
                    reject(error);
                }
            };

            reader.onerror = (error) => {
                console.error("Error leyendo archivo KMZ:", error);
                reject(error);
            };

            reader.readAsArrayBuffer(file);
        });
    },
    async addLayerShapefileZip(file) {
        console.log("Agregando SHP dentro de ZIP:", file.name);

        const id = crypto.randomUUID();
        const zip = await JSZip.loadAsync(await file.arrayBuffer());

        // Buscar los archivos del shapefile dentro del ZIP
        const shpFile = Object.keys(zip.files).find(f => f.endsWith(".shp"));
        const shxFile = Object.keys(zip.files).find(f => f.endsWith(".shx"));
        const dbfFile = Object.keys(zip.files).find(f => f.endsWith(".dbf"));

        if (!shpFile || !shxFile || !dbfFile) {
            console.error("ZIP incompleto: faltan archivos shapefile");
            return;
        }

        // Leer contenido
        const shpBuf = await zip.files[shpFile].async("arraybuffer");
        const shxBuf = await zip.files[shxFile].async("arraybuffer");
        const dbfBuf = await zip.files[dbfFile].async("arraybuffer");

        // Convertir SHP → GeoJSON
        const geojson = await shapefile.read(shpBuf, dbfBuf);

        const features = new GeoJSON().readFeatures(geojson, {
            featureProjection: "EPSG:3857",
        });

        const vectorSource = new VectorSource({ features });

        const layer = new VectorLayer({
            source: vectorSource,
            name: id,
        });

        mapService.get().addLayer(layer);

        const extent = vectorSource.getExtent();
        mapService.zoomToExtent(extent);

        console.log("Capa SHP cargada correctamente.");
        return layer;
    },
    async addLayerShp(file) {
        console.log("Agregando capa SHP desde archivo:", file.name);

        const id = crypto.randomUUID();
        const arrayBuffer = await file.arrayBuffer();

        // Convertir SHP → GeoJSON
        const geojson = await shapefile.read(arrayBuffer);

        const features = new GeoJSON().readFeatures(geojson, {
            featureProjection: "EPSG:3857",
        });

        const vectorSource = new VectorSource({ features });

        const layer = new VectorLayer({
            source: vectorSource,
            name: id,
        });

        mapService.get().addLayer(layer);

        const extent = vectorSource.getExtent();
        mapService.zoomToExtent(extent);

        console.log("Capa SHP cargada correctamente.");
        return layer;
    },
    async uploadShpKmz(file, idMap) {
        console.log("Subiendo archivo...", file.name, idMap);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("idMap", idMap);
        const url = process.env.VUE_APP_API_URL + "api/v1/uploads";
        console.log("URL para subida:", url);
        try {
            const response = await fetch(url, {
                method: "POST",
                body: formData,
            });
            console.log("Response:", response);
            return await response.json();
        } catch (error) {
            console.error("Error al subir archivo:", error);
            throw error;
        }
    },
    removeLayerByID(layerID) {
        mapService.removeLayerFromMapByID(layerID);
    },
    async getLayerTypeArcgis(layer) {
        if (this.getLayerProvider(layer) !== "arcgis") {
            return "";
        }
        const layerUrl = UrlHandler.getArcgisUrl(layer);
        const metadataUrl = layerUrl + "?f=pjson";
        const metadata = await fetch(metadataUrl).then(response => response.json());
        return metadata.geometryType || "";
    },
    async filterLayersByInfluence(categories, influenceType) {
        const typeLayerDictionary = { "esriGeometryPoint": "Radial", "esriGeometryPolygon": "Poligonal", "esriGeometryPolyline": "Lineal" }
        let categoriesFiltered = [];
        for (const category of categories) {
            let layersFiltered = [];
            for (const layer of category.layers) {
                const layerTypeArcgis = await this.getLayerTypeArcgis(layer);
                layer.type = layerTypeArcgis;
                if (layerTypeArcgis === "") {
                    continue;
                }
                const typeLayer = typeLayerDictionary[layerTypeArcgis];
                if (typeLayer !== influenceType) {
                    continue;
                }
                layersFiltered.push(layer);
            }
            if (layersFiltered.length === 0) {
                continue;
            }
            categoriesFiltered.push({
                name: category.name,
                layers: layersFiltered
            });
        }
        return categoriesFiltered;
    },
    layerExists(layerId) {
        return mapService.layerExists(layerId);
    },
    async getUploadedFilesByMap(idMapa) {
        console.log("Obteniendo archivos subidos desde el backend...", idMapa);
        const url = process.env.VUE_APP_API_URL + "api/v1/uploads/map/" + idMapa;
        console.log("URL para obtener archivos subidos:", url);
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error("Error en la petición: " + response.statusText);
            }
            const data = await response.json();
            console.log("Data del upload:", data);
            return data;
        } catch (error) {
            console.error("Error al obtener archivos subidos:", error);
            return [];
        }
    },
    async deleteFileByID(fileId) {
        const url = process.env.VUE_APP_API_URL + "api/v1/uploads/" + fileId;
        console.log("URL para eliminar archivo:", url);
        try {
            const response = await fetch(url, {
                method: "DELETE",
            });
            if (!response.ok) {
                throw new Error("Error en la petición: " + response.statusText);
            }
            console.log("Archivo eliminado correctamente.");
        } catch (error) {
            console.error("Error al eliminar archivo:", error);
            throw error; // Re-throw to allow component to handle fallback (though component currently catches without showing error to user, but logic flow relies on completion)
        }
    },
    async loadLayerFromBackend(fileObj) {
        const url = process.env.VUE_APP_API_URL + "api/v1/uploads/" + fileObj.id + "/download";
        console.log("Descargando capa desde:", url);
        try {
            const response = await fetch(url);
            if (!response.ok) throw new Error("Fallo la descarga del archivo");
            const blob = await response.blob();
            const file = new File([blob], fileObj.name);

            if (file.name.toLowerCase().endsWith(".zip")) {
                return await this.addLayerShapefileZip(file);
            } else if (file.name.toLowerCase().endsWith(".kmz")) {
                return await this.addLayerKmz(file);
            } else if (file.name.toLowerCase().endsWith(".kml")) {
                return await this.addLayerKml(file);
            } else if (file.name.toLowerCase().endsWith(".shp")) {
                return await this.addLayerShp(file);
            }
        } catch (e) {
            console.error("Error cargando capa desde backend:", e);
            throw e;
        }
    },
    async getPoblationLayers(poblationType) {
        const url = process.env.VUE_APP_API_URL + "api/v1/layers/poblation/" + poblationType;
        console.log("URL para obtener capas de población:", url);
        try {
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) {
                throw new Error("Error en la petición: " + response.statusText);
            }
            const data = await response.json();
            console.log("Data de capas de población:", data);
            return data;
        } catch (error) {
            console.error("Error al obtener capas de población:", error);
            return [];
        }
    },
    async getVulnerableLayers() {
        const url = process.env.VUE_APP_API_URL + "api/v1/layers/vulnerable";
        console.log("URL para obtener capas vulnerables:", url);
        try {
            const response = await fetch(url, { credentials: 'include' });
            if (!response.ok) {
                throw new Error("Error en la petición: " + response.statusText);
            }
            const data = await response.json();
            console.log("Data de capas vulnerables:", data);
            return data;
        } catch (error) {
            console.error("Error al obtener capas vulnerables:", error);
            return [];
        }
    }
};